import {
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  parseEntityRef,
} from '@backstage/catalog-model';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Tooltip } from '@material-ui/core';
import {
  EntityRefLink,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';

/** A ref as an object, or undefined when the string is not a valid ref. */
function toCompoundRef(
  entityRef: CompoundEntityRef | string,
  defaultKind: string,
): CompoundEntityRef | undefined {
  if (typeof entityRef !== 'string') {
    return entityRef;
  }
  try {
    return parseEntityRef(entityRef, {
      defaultKind,
      defaultNamespace: DEFAULT_NAMESPACE,
    });
  } catch {
    return undefined;
  }
}

/**
 * The name a person reads for an entity ref: the entity's display name or
 * title when the catalog has one, otherwise just its name. Never a raw ref
 * such as `group:default/squad-x` or `user:guest` (MC-2).
 */
export function readableName(
  title: string,
  entityRef: CompoundEntityRef,
): string {
  const { kind, namespace, name } = entityRef;
  const refForms = [
    `${kind}:${namespace}/${name}`,
    `${kind}:${name}`,
    `${namespace}/${name}`,
  ].map(form => form.toLocaleLowerCase('en-US'));
  return refForms.includes(title.toLocaleLowerCase('en-US')) ? name : title;
}

const refKey = ({ kind, namespace, name }: CompoundEntityRef) =>
  `${kind}:${namespace}/${name}`.toLocaleLowerCase('en-US');

function DisplayLink(
  props: Readonly<{
    entityRef: CompoundEntityRef;
    defaultKind: string;
    onName?: (key: string, name: string) => void;
    tooltip?: string;
  }>,
) {
  const { entityRef, defaultKind, onName, tooltip } = props;
  const { primaryTitle } = useEntityPresentation(entityRef, { defaultKind });
  const name = readableName(primaryTitle, entityRef);
  const key = refKey(entityRef);
  useEffect(() => {
    onName?.(key, name);
  }, [onName, key, name]);
  const link = (
    <EntityRefLink entityRef={entityRef} defaultKind={defaultKind}>
      {name}
    </EntityRefLink>
  );
  // On the link itself, so it opens on hover and on keyboard focus.
  return tooltip ? <Tooltip title={tooltip}>{link}</Tooltip> : link;
}

/**
 * Links to one or more entities by display name, for a cell that may cut
 * them with an ellipsis: each link carries a tooltip with every full name,
 * shown on hover and on keyboard focus (UI spec v2).
 */
export function EntityDisplayLinks(
  props: Readonly<{
    entityRefs: CompoundEntityRef[];
    defaultKind?: string;
    className?: string;
  }>,
) {
  const { entityRefs, defaultKind = 'group', className } = props;
  const [names, setNames] = useState<Record<string, string>>({});
  const onName = useCallback(
    (key: string, name: string) =>
      setNames(previous =>
        previous[key] === name ? previous : { ...previous, [key]: name },
      ),
    [],
  );
  const title = entityRefs
    .map(ref => names[refKey(ref)] ?? ref.name)
    .join(', ');
  return (
    <span className={className}>
      {entityRefs.map((ref, index) => (
        <Fragment key={refKey(ref)}>
          {index > 0 && ', '}
          <DisplayLink
            entityRef={ref}
            defaultKind={defaultKind}
            onName={onName}
            tooltip={title}
          />
        </Fragment>
      ))}
    </span>
  );
}

/**
 * Link to an entity's page labelled with its display name, through the
 * entity presentation API (batched lookups, display name only). An invalid
 * ref renders nothing.
 */
export function EntityDisplayLink(
  props: Readonly<{
    entityRef: CompoundEntityRef | string;
    defaultKind?: string;
  }>,
) {
  const { entityRef, defaultKind = 'group' } = props;
  const ref = toCompoundRef(entityRef, defaultKind);
  return ref ? <DisplayLink entityRef={ref} defaultKind={defaultKind} /> : null;
}
